<script setup>
import { ref, computed, watch, nextTick } from "vue";
import _ from "lodash";
import CourseApprovalModal from "../CourseApprovalModal.vue";
import SimpleTextDialog from "../SimpleTextDialog.vue";
import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import PencilIcon from "@/components/shared/icons/PencilIcon.vue";
import { formatCreditAmount } from "@/components/ExperiencePage/Algolia/ResultCards/ClassesCard/utils";
import { DescriptionIcon, FillIcon, SyllabusIcon } from "@/components/shared/icons";
import CourseMatchPill from "./CourseMatchPill.vue";

const modalOpen = ref(false);
const descriptionModalOpen = ref(false);
const props = defineProps({
  row: { type: Object, required: true },
  showApprovals: { type: Boolean, default: false },
});
const descriptionButton = ref(null);
watch(descriptionModalOpen, (newValue) => {
  if (!newValue) {
    nextTick(() => {
      descriptionButton.value?.focus();
    });
  }
});

const savedApproveCoursesList = ref(_.cloneDeep(props?.row?.equivalents));

const approveCode = computed(() => {
  let code = savedApproveCoursesList.value[0]?.homeClassId || "";
  if (savedApproveCoursesList.value.length > 1) {
    code += ` + ${savedApproveCoursesList.value.length - 1}`;
  }
  return code;
});

const updateCourseData = (courseObjects) => {
  savedApproveCoursesList.value = courseObjects;
};

const recommendedUsCreditAmount = computed(() => {
  const rawFormattedRecord = formatCreditAmount(
    props?.row?.recommendedUsCreditAmount || 0
  );
  let formattedRecord = String(rawFormattedRecord)
    .replace(/\.0$/, '') // Remove any trailing ".0"
    .replace(/^0$/, ''); // If the returned value is "0", return a blank string
  return formattedRecord;
});

</script>

<template>
  <CourseApprovalModal
    :modal-is-open="modalOpen"
    :row="props.row"
    :saved-approved-courses="savedApproveCoursesList"
    @allCardsDataBus="updateCourseData"
    @closeModal="modalOpen = false"
  />

  <tr class="bg-white">
    <td class="px-4 py-5 border-t border-b border-gray-300">
      <CourseMatchPill :similarity=props.row.rank />
    </td>
    <td class="px-4 py-5 border-t border-b border-gray-300 max-w-md">
      {{ props.row.title }}
    </td>
    <td class="px-4 py-5 border-t border-b border-gray-300">
      {{ props.row.hostInstitutionName }}
    </td>
    <td class="px-4 py-5 border-t border-b border-gray-300">
      {{ props.row.cityAndCountry }}
    </td>
    <td class="px-4 py-5 border-t justify-center border-b border-gray-300">
      <div class="flex items-center justify-center">
        {{ props.row.credits }}
      </div>
    </td>
    <td class="px-4 py-5 border-t justify-center border-b border-gray-300">
      <div class="flex items-center justify-center">
        {{ recommendedUsCreditAmount }}
      </div>
    </td>
    <td class="px-4 py-5 border-t border-b border-gray-300">
      <div class="flex items-center justify-start h-full">
        <button
          ref="descriptionButton"
          aria-haspopup="dialog"
          class="items-center flex flex-row font-semibold text-center text-university-secondary px-6 py-3"
          @click="descriptionModalOpen = true"
        >
          <FillIcon title="Description Modal Open Icon" class="mr-1 w-6 h-6" />
        </button>
      </div>
    </td>
    <td class="px-4 py-5 border-t border-b border-gray-300">
      <div class="flex items-center cursor-pointer justify-start">
        <a v-if="props.row.syllabus" target="_blank" :href="`${props.row.syllabus}`">
          <button class="items-center flex flex-row text-university-secondary px-6 py-3" aria-label="Syllabus">
            <SyllabusIcon class="mr-1 w-6 h-6" title="Syllabus" />
          </button>
        </a>
      </div>
    </td>
    <td class="px-4 py-5 border-t border-b border-gray-300 border-r">
      {{ approveCode || "" }}
    </td>
    <td v-if="showApprovals" class="px-4 py-5 border-t border-b border-gray-300">
      <div class="flex items-center justify-center h-full">
        <button
          v-if="approveCode === ''"
          class="items-center flex flex-row font-semibold text-center text-university-secondary px-6 py-3"
          @click="modalOpen = true"
        >
          <PlusIcon class="mr-1 w-6 h-6" />
        </button>
        <button
          v-else
          class="items-center flex flex-row font-semibold text-center text-university-secondary px-6 py-3"
          @click="modalOpen = true"
        >
          <PencilIcon class="mr-1 w-4 h-4" />
        </button>
      </div>
    </td>
  </tr>

  <SimpleTextDialog v-model="descriptionModalOpen">
    <template #header>
      <div class="flex items-center gap-2">
        <DescriptionIcon
          title="Folded Page Icon"
          size="36"
          class="mr-1 w-6 h-6"
          stroke-width="1"
          :fill="'text-university-primary'"
          :stroke="'text-university-primary'"
        />
        <h2 class="m-0 text-lg uppercase text-university-primary">DESCRIPTION FOR {{ props.row.title }}</h2>
      </div>
    </template>

    <div>
      <p>{{ props.row.description }}</p>
    </div>

    <template #footer>
      <button
        class="px-8 py-2 font-bold text-university-secondary bg-transparent border-2 border-current rounded-none"
        @click="descriptionModalOpen = false"
      >
        CLOSE
      </button>
    </template>
  </SimpleTextDialog>
</template>
